'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  AlertTriangle,
  Zap,
  Network,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  Users,
  BarChart3,
  Shield,
  HelpCircle,
  LogOut,
  UserCog,
  GitBranch,
  ClipboardList,
  ClipboardCheck,
  Globe,
  BookOpen,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  dynamicBadgeKey?: string;
  group: string;
}

const navItems: NavItem[] = [
  {
    id: 'nav-dashboard',
    label: 'Network Dashboard',
    href: '/network-operations-dashboard',
    icon: LayoutDashboard,
    group: 'Operations',
  },
  {
    id: 'nav-incidents',
    label: 'Incident Management',
    href: '/incident-management',
    icon: AlertTriangle,
    badge: 7,
    group: 'Operations',
  },
  {
    id: 'nav-automation',
    label: 'Automation & Integration',
    href: '/automation-integration',
    icon: Zap,
    group: 'Operations',
  },
  {
    id: 'nav-workflows',
    label: 'Automation Workflows',
    href: '/automation-workflows',
    icon: GitBranch,
    group: 'Operations',
  },
  {
    id: 'nav-work-orders',
    label: 'Work Order Allocation',
    href: '/work-order-allocation',
    icon: ClipboardList,
    dynamicBadgeKey: 'sidebar_badge_allocation',
    group: 'Operations',
  },
  {
    id: 'nav-work-order-execution',
    label: 'Work Order Execution',
    href: '/work-order-execution',
    icon: ClipboardCheck,
    dynamicBadgeKey: 'sidebar_badge_execution',
    group: 'Operations',
  },
  {
    id: 'nav-team',
    label: 'Team',
    href: '/team',
    icon: UserCog,
    group: 'Operations',
  },
  {
    id: 'nav-incident-status',
    label: 'Customer Status Portal',
    href: '/incident-status',
    icon: Globe,
    group: 'Operations',
  },
  {
    id: 'nav-onboarding',
    label: 'Onboarding',
    href: '/onboarding',
    icon: BookOpen,
    group: 'Operations',
  },
  {
    id: 'nav-topology',
    label: 'Network Topology',
    href: '#',
    icon: Network,
    group: 'Observability',
  },
  {
    id: 'nav-analytics',
    label: 'Analytics & Reports',
    href: '#',
    icon: BarChart3,
    group: 'Observability',
  },
  {
    id: 'nav-alerts',
    label: 'Alert Rules',
    href: '#',
    icon: Bell,
    badge: 3,
    group: 'Observability',
  },
  {
    id: 'nav-tenants',
    label: 'Tenants',
    href: '#',
    icon: Users,
    group: 'Administration',
  },
  {
    id: 'nav-security',
    label: 'Security',
    href: '#',
    icon: Shield,
    group: 'Administration',
  },
  {
    id: 'nav-settings',
    label: 'Settings',
    href: '#',
    icon: Settings,
    group: 'Administration',
  },
];

const groups = ['Operations', 'Observability', 'Administration'];

// Default dynamic badge values (unassigned WOs + active/breaching execution WOs)
const DEFAULT_DYNAMIC_BADGES: Record<string, number> = {
  sidebar_badge_allocation: 5, // unassigned work orders
  sidebar_badge_execution: 2,  // active + SLA-breaching execution orders
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [dynamicBadges, setDynamicBadges] = useState<Record<string, number>>(DEFAULT_DYNAMIC_BADGES);
  const pathname = usePathname();

  // Listen for badge updates broadcast from pages via storage events
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key && e.key.startsWith('sidebar_badge_') && e.newValue !== null) {
        setDynamicBadges((prev) => ({ ...prev, [e.key!]: parseInt(e.newValue!, 10) || 0 }));
      }
    };
    // Also read current values from localStorage on mount
    const allKeys = ['sidebar_badge_allocation', 'sidebar_badge_execution'];
    const stored: Record<string, number> = {};
    allKeys.forEach((key) => {
      const val = localStorage.getItem(key);
      if (val !== null) stored[key] = parseInt(val, 10) || 0;
    });
    if (Object.keys(stored).length > 0) {
      setDynamicBadges((prev) => ({ ...prev, ...stored }));
    }

    window.addEventListener('storage', handleStorage);
    // Also listen for same-tab custom events
    const handleCustom = (e: Event) => {
      const ce = e as CustomEvent<{ key: string; value: number }>;
      setDynamicBadges((prev) => ({ ...prev, [ce.detail.key]: ce.detail.value }));
    };
    window.addEventListener('sidebar-badge-update', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('sidebar-badge-update', handleCustom);
    };
  }, []);

  const getBadge = (item: NavItem): number | undefined => {
    if (item.dynamicBadgeKey) {
      const val = dynamicBadges[item.dynamicBadgeKey];
      return val && val > 0 ? val : undefined;
    }
    return item.badge;
  };

  return (
    <aside
      className="relative flex flex-col h-screen bg-surface border-r border-border transition-all duration-300 ease-in-out flex-shrink-0"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-border overflow-hidden">
        <div className="flex items-center gap-2 min-w-0">
          <AppLogo size={28} />
          {!collapsed && (
            <span className="font-semibold text-sm text-foreground tracking-tight whitespace-nowrap">
              NablixAI
            </span>
          )}
        </div>
      </div>

      {/* Tenant badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
          <p className="text-[10px] font-mono text-primary/70 uppercase tracking-widest">Tenant</p>
          <p className="text-xs font-semibold text-primary truncate">Nablix Air</p>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 scrollbar-hide">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="px-4 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted">
                  {group}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;
                const badge = getBadge(item);
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`relative flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group
                      ${isActive
                        ? 'bg-primary/15 text-primary' :'text-foreground-muted hover:bg-surface-raised hover:text-foreground'
                      }`}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                    {badge && !collapsed && (
                      <span className="ml-auto text-[10px] font-bold bg-danger/20 text-danger px-1.5 py-0.5 rounded-full">
                        {badge}
                      </span>
                    )}
                    {badge && collapsed && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-danger" />
                    )}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-3 space-y-1">
        <button
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-foreground-muted hover:bg-surface-raised hover:text-foreground transition-all duration-150"
          title={collapsed ? 'Help' : undefined}
        >
          <HelpCircle size={18} className="flex-shrink-0" />
          {!collapsed && <span>Help & Support</span>}
        </button>
        <button
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-foreground-muted hover:bg-surface-raised hover:text-foreground transition-all duration-150"
          title={collapsed ? 'Sign out' : undefined}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 py-2 mt-2">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
              DT
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">Dawit Taddese</p>
              <p className="text-[10px] text-muted truncate">Network Engineer</p>
            </div>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-surface-raised border border-border flex items-center justify-center text-muted hover:text-foreground hover:border-primary transition-all duration-150 z-10"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}